import React, {useMemo} from 'react';
import {MenuItem, MenuItemOption, ProductExtraOption} from '../types';
import {useCart} from '../context/CartContext';
import {useMerchant} from '../context/MerchantContext';
import {
    CloseButton as GlobalCloseButton,
    ModalBody,
    ModalFooter,
    ModalHeader as GlobalModalHeader,
    ModalTitle as GlobalModalTitle
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';
import QuantitySelector, {QtyButton, QtyValue, QuantitySelectorContainer} from './common/QuantitySelector';
import ProductImage from './common/ProductImage';

// Hooks
import { useProductOptions } from '../hooks/useProductOptions';
import { trackProductViewed } from '../services/SuggestionService';

// Styles
import * as S from './product/ProductModalStyles';

interface ProductModalProps {
    item: MenuItem;
    isOpen: boolean;
    onClose: () => void;
    editMode?: boolean;
    editIndex?: number;
}

// Format price to Brazilian Real
const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(price);
};

const getDayName = (day: number) => {
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return days[day] || '';
};

const ProductModal: React.FC<ProductModalProps> = ({item, isOpen, onClose, editMode = false, editIndex}) => {
    const {addItem, removeItem, state} = useCart();
    const {restaurant, merchantId} = useMerchant();

    React.useEffect(() => {
        if (isOpen && item?.id && merchantId) {
            trackProductViewed(merchantId, item.id);
        }
    }, [isOpen, item?.id, merchantId]);

    const cartItemToEdit = useMemo(() => {
        if (editMode && typeof editIndex === 'number') {
            return state.items[editIndex];
        }
        return null;
    }, [editMode, editIndex, state.items]);

    const {
        selectedOptions,
        setSelectedOptions,
        notes,
        setNotes,
        quantity,
        setQuantity,
        isDescriptionExpanded,
        setIsDescriptionExpanded,
        totalPrice,
        toggleOption,
        areAllGroupsValid
    } = useProductOptions(item, isOpen, cartItemToEdit?.quantity || 1, cartItemToEdit?.notes || '');

    const comboItemsSummary = useMemo(() => {
        if (item.productType !== 'COMBO' || !item.subProducts || item.subProducts.length === 0) return [];

        return item.subProducts
            .map((subProduct, index) => {
                const productName = subProduct.name?.trim();
                if (!productName) return null;

                return {
                    id: subProduct.id || subProduct.productId || `${item.id}-${index}`,
                    name: productName,
                    description: subProduct.description,
                    quantity: subProduct.quantity || 0,
                    imageUrl: subProduct.imageUrl,
                    price: subProduct.price,
                };
            })
            .filter(Boolean) as Array<{
                id: string;
                name: string;
                description?: string;
                quantity: number;
                imageUrl?: string;
                price?: number;
            }>;
    }, [item.id, item.productType, item.subProducts]);

    // Synchronize selected options if editing
    React.useEffect(() => {
        if (isOpen && editMode && cartItemToEdit && item.extras) {
            const initialSelected: Record<string, Record<string, number>> = {};
            item.extras.forEach(extra => {
                const quantities: Record<string, number> = {};
                extra.options.forEach(option => {
                    const cartOptions = cartItemToEdit.options?.filter(opt => opt.id === option.id) || [];
                    if (cartOptions.length > 0) {
                        quantities[option.id] = cartOptions.length;
                    }
                });
                initialSelected[extra.id] = quantities;
            });
            setSelectedOptions(initialSelected);
        }
    }, [isOpen, editMode, cartItemToEdit, item.extras, setSelectedOptions]);

    const isAvailable = item.available && 
        item.status !== 'UNAVAILABLE' &&
        (restaurant?.stockControlEnabled !== true || !item.inventory?.stockControl || item.inventory.stockQuantity > 0 || !item.inventory.blockWithoutStock);

    const isFormValid = isAvailable && areAllGroupsValid();

    const handleOptionSelect = (extraId: string, option: ProductExtraOption, change: number = 1) => {
        const extraGroup = item.extras?.find(extra => extra.id === extraId);
        if (!extraGroup || !option.available) return;

        setSelectedOptions(prev => {
            const currentGroup = {...(prev[extraId] || {})};
            const currentQty = currentGroup[option.id] || 0;
            const newQty = Math.max(0, currentQty + change);
            const totalInGroup = Object.values(currentGroup).reduce((sum, q) => sum + q, 0);

            if (extraGroup.maxSelections === 1) {
                if (newQty > 0) return { ...prev, [extraId]: {[option.id]: 1} };
                return { ...prev, [extraId]: {} };
            } else {
                if (change > 0 && totalInGroup >= extraGroup.maxSelections) return prev;
                if (newQty === 0) delete currentGroup[option.id];
                else currentGroup[option.id] = newQty;
                return { ...prev, [extraId]: currentGroup };
            }
        });
    };

    const handleAddToCart = () => {
        const options: MenuItemOption[] = [];
        Object.entries(selectedOptions).forEach(([extraId, optionsMap]) => {
            const extraGroup = item.extras?.find(e => e.id === extraId);
            if (!extraGroup) return;
            Object.entries(optionsMap).forEach(([optionId, qty]) => {
                const option = extraGroup.options.find(o => o.id === optionId);
                if (option) {
                    for (let i = 0; i < qty; i++) {
                        options.push({
                            id: option.id,
                            name: option.name,
                            description: option.description,
                            price: option.price || 0,
                            available: option.available,
                            imageUrl: option.imageUrl
                        });
                    }
                }
            });
        });

        if (editMode && typeof editIndex === 'number') {
            removeItem(editIndex);
        }
        addItem(item, quantity, options, notes);
        onClose();
    };

    if (!isOpen || !item) return null;

    // Translation maps
    const allergenMap: Record<string, string> = {
        'GLUTEN': 'Contém Glúten', 'MILK': 'Contém Leite', 'EGG': 'Contém Ovo',
        'SOY': 'Contém Soja', 'PEANUT': 'Contém Amendoim', 'NUTS': 'Contém Castanhas',
        'FISH': 'Contém Peixe', 'CRUSTACEANS': 'Contém Crustáceos', 'OTHERS': 'Outros'
    };
    const labelMap: Record<string, string> = {
        'VEGAN': 'Vegano', 'VEGETARIAN': 'Vegetariano', 'LACTOSE_FREE': 'Sem Lactose',
        'GLUTEN_FREE': 'Sem Glúten', 'LOW_CARB': 'Low Carb', 'DIET': 'Diet', 'WHOLE_GRAIN': 'Integral'
    };
    const dietaryRestrictionMap: Record<string, string> = {
        'ALCOHOLIC_DRINK': 'Bebida Alcoólica', 'VEGAN': 'Vegano', 'VEGETARIAN': 'Vegetariano',
        'LACTOSE_FREE': 'Sem Lactose', 'GLUTEN_FREE': 'Sem Glúten'
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} maxWidth="800px" noPadding zIndex={10000}>
            <GlobalModalHeader sticky>
                <div className="d-flex align-items-center w-100">
                    <GlobalCloseButton onClick={onClose} style={{ position: 'relative', left: '0', background: 'none', width: '32px', height: '32px', boxShadow: 'none', marginRight: '0.5rem' }} className="d-flex d-sm-none">
                        <i className="fas fa-arrow-left" style={{ fontSize: '1.2rem', color: '#2d3748' }}></i>
                    </GlobalCloseButton>
                    <GlobalModalTitle style={{ textAlign: 'left', fontSize: '1.2rem' }}>{editMode ? `Editar ${item.name}` : item.name}</GlobalModalTitle>
                    <GlobalCloseButton onClick={onClose} className="d-none d-sm-flex">×</GlobalCloseButton>
                </div>
            </GlobalModalHeader>

            <ModalBody>
                <S.MainContentLayout>
                    <S.LeftColumn>
                        <S.ImageContainer>
                            <S.ProductImg src={item.image} alt={item.name} />
                        </S.ImageContainer>

                        {item.dietaryRestriction && (
                            <S.BadgeContainer>
                                <S.DietaryBadge type={item.dietaryRestriction}>
                                    {dietaryRestrictionMap[item.dietaryRestriction] || item.dietaryRestriction}
                                </S.DietaryBadge>
                            </S.BadgeContainer>
                        )}

                        {item.productType === 'COMBO' && (
                            <S.ComboSummaryCard>
                                <S.ComboSummaryHeader>
                                    <div>
                                        <S.ComboSummaryTitle>Itens que compõem o combo</S.ComboSummaryTitle>
                                        <S.ComboSummaryHint>
                                            Veja os produtos e a quantidade de cada um que compõe este combo.
                                        </S.ComboSummaryHint>
                                    </div>
                                    <S.RequiredBadge style={{ backgroundColor: '#1f2937' }}>Combo</S.RequiredBadge>
                                </S.ComboSummaryHeader>

                                {comboItemsSummary.length > 0 ? (
                                    <S.ComboSummaryList>
                                        {comboItemsSummary.map(row => (
                                            <S.ComboSummaryRow key={row.id}>
                                                <div className="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                                                    <div
                                                        className="flex-shrink-0 rounded-3 overflow-hidden"
                                                        style={{
                                                            width: '48px',
                                                            height: '48px',
                                                            background: '#f8fafc',
                                                            border: '1px solid #edf2f7'
                                                        }}
                                                    >
                                                        <ProductImage
                                                            src={row.imageUrl}
                                                            alt={row.name}
                                                            className="w-100 h-100 object-fit-cover"
                                                        />
                                                    </div>
                                                    <S.ComboSummaryRowLeft>
                                                        <S.ComboSummaryName>{row.name}</S.ComboSummaryName>
                                                        <S.ComboSummaryMeta>
                                                            {row.description && row.description !== '0' ? row.description : 'Produto do combo'}
                                                        </S.ComboSummaryMeta>
                                                    </S.ComboSummaryRowLeft>
                                                </div>
                                                <S.ComboSummaryQty accent={restaurant?.theme?.primary}>
                                                    {row.quantity}x
                                                </S.ComboSummaryQty>
                                            </S.ComboSummaryRow>
                                        ))}
                                    </S.ComboSummaryList>
                                ) : (
                                    <S.ComboSummaryEmpty>
                                        Selecione os itens do combo para visualizar aqui os produtos e suas quantidades.
                                    </S.ComboSummaryEmpty>
                                )}
                            </S.ComboSummaryCard>
                        )}

                        <S.ProductDescription 
                            className={!isDescriptionExpanded ? 'text-truncate-5' : ''}
                            dangerouslySetInnerHTML={{ __html: item.description || 'Sem descrição disponível.' }}
                        />
                        
                        {item.description && item.description.length > 200 && (
                            <S.ShowMoreButton onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
                                {isDescriptionExpanded ? <>Ver menos <i className="fas fa-chevron-up"></i></> : <>Ver mais <i className="fas fa-chevron-down"></i></>}
                            </S.ShowMoreButton>
                        )}

                        {item.nutritionalInfo && ((item.nutritionalInfo.allergens?.length || 0) > 0 || (item.nutritionalInfo.labels?.length || 0) > 0) && (
                            <div className="bg-light p-3 rounded-3 mb-4">
                                <h6 className="fw-bold mb-2 small"><i className="fas fa-info-circle me-1"></i> Informações Nutricionais</h6>
                                {item.nutritionalInfo.allergens?.map((a, i) => (
                                    <span key={i} className="badge bg-white text-dark border me-1 small mb-1">{allergenMap[a] || a}</span>
                                ))}
                                {item.nutritionalInfo.labels?.map((l, i) => (
                                    <span key={i} className="badge bg-success-subtle text-success border-success-subtle me-1 small mb-1">{labelMap[l] || l}</span>
                                ))}
                            </div>
                        )}

                        <S.DesktopNotesSection>
                            <S.ExtraGroupTitle style={{ fontSize: '1.2rem', marginBottom: '10px' }}>Observações</S.ExtraGroupTitle>
                            <S.NotesTextarea
                                placeholder="Alguma observação para o seu pedido? Ex: sem cebola, bem passado, etc."
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                            />
                        </S.DesktopNotesSection>
                    </S.LeftColumn>

                    <S.RightColumn>
                        {item.extras?.map(extra => (
                            <S.ExtraGroup key={extra.id}>
                                <S.ExtraGroupHeader>
                                    <div>
                                        <S.ExtraGroupTitle>{extra.name}</S.ExtraGroupTitle>
                                        <div className="small text-muted mt-1">
                                            {extra.required 
                                                ? (extra.maxSelections === 1 ? 'Escolha 1 opção' : `Escolha até ${extra.maxSelections} opções`)
                                                : `Escolha até ${extra.maxSelections} opções`
                                            }
                                        </div>
                                    </div>
                                    {extra.required && <S.RequiredBadge>Obrigatório</S.RequiredBadge>}
                                </S.ExtraGroupHeader>

                                {extra.options.map(option => (
                                    <S.ExtraOption key={option.id} selected={(selectedOptions[extra.id]?.[option.id] || 0) > 0} available={option.available}
                                        onClick={() => option.available && extra.maxSelections === 1 && handleOptionSelect(extra.id, option)}
                                    >
                                        <S.ExtraOptionContent>
                                            {option.imageUrl && (
                                                <S.ExtraOptionImageWrap>
                                                    <ProductImage src={option.imageUrl} alt={option.name} />
                                                </S.ExtraOptionImageWrap>
                                            )}
                                            <div className="flex-grow-1 min-w-0">
                                                <div className="fw-bold small">{option.name} {!option.available && <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }}>EM FALTA</span>}</div>
                                                {option.description && option.description !== "0" && <div className="small text-muted" dangerouslySetInnerHTML={{ __html: option.description }} />}
                                                <S.OptionPrice>{option.price ? `+ ${formatPrice(option.price)}` : formatPrice(0)}</S.OptionPrice>
                                            </div>
                                        </S.ExtraOptionContent>

                                        <div className="ms-3">
                                            {extra.maxSelections === 1 ? (
                                                <S.RadioIcon 
                                                    selected={(selectedOptions[extra.id]?.[option.id] || 0) > 0} 
                                                    className={`far ${(selectedOptions[extra.id]?.[option.id] || 0) > 0 ? 'fa-dot-circle' : 'fa-circle'}`} 
                                                />
                                            ) : (
                                                <QuantitySelectorContainer style={{ padding: '4px 8px', gap: '0.5rem' }}>
                                                    <QtyButton 
                                                        onClick={(e) => { e.stopPropagation(); handleOptionSelect(extra.id, option, -1); }} 
                                                        disabled={(selectedOptions[extra.id]?.[option.id] || 0) <= 0}
                                                        style={{ width: '24px', height: '24px', fontSize: '0.8rem' }}
                                                    >
                                                        <i className="fas fa-minus"></i>
                                                    </QtyButton>
                                                    <QtyValue style={{ fontSize: '0.9rem', minWidth: '15px' }}>{selectedOptions[extra.id]?.[option.id] || 0}</QtyValue>
                                                    <QtyButton 
                                                        onClick={(e) => { e.stopPropagation(); handleOptionSelect(extra.id, option, 1); }}
                                                        style={{ width: '24px', height: '24px', fontSize: '0.8rem' }}
                                                    >
                                                        <i className="fas fa-plus"></i>
                                                    </QtyButton>
                                                </QuantitySelectorContainer>
                                            )}
                                        </div>
                                    </S.ExtraOption>
                                ))}
                            </S.ExtraGroup>
                        ))}

                        <S.MobileNotesSection>
                            <S.ExtraGroupTitle style={{ fontSize: '1.2rem', marginBottom: '10px' }}>Observações</S.ExtraGroupTitle>
                            <S.NotesTextarea
                                placeholder="Alguma observação para o seu pedido? Ex: sem cebola, bem passado, etc."
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                            />
                        </S.MobileNotesSection>
                    </S.RightColumn>
                </S.MainContentLayout>
            </ModalBody>

            <ModalFooter mobileFlexRow={true}>
                <div className="d-flex align-items-center justify-content-between w-100 gap-2 gap-sm-3">
                    <div className="flex-shrink-0">
                        <QuantitySelector 
                            quantity={quantity}
                            onDecrease={() => setQuantity(Math.max(1, quantity - 1))}
                            onIncrease={() => setQuantity(quantity + 1)}
                            maxQuantity={restaurant?.stockControlEnabled === true && item.inventory?.stockControl && item.inventory.blockWithoutStock ? item.inventory.stockQuantity : undefined}
                        />
                    </div>

                    <S.AddToCartButton onClick={handleAddToCart} disabled={!isFormValid}>
                        <span>
                            {isAvailable
                                ? (restaurant?.stockControlEnabled !== true || !item.inventory?.stockControl || !item.inventory.blockWithoutStock || (item.inventory.stockQuantity !== undefined && item.inventory.stockQuantity >= quantity)
                                    ? (editMode ? 'Atualizar' : 'Adicionar ao carrinho')
                                    : 'Sem estoque suficiente')
                                : 'Produto Esgotado'}
                        </span>
                        <span className="ms-2">{formatPrice(totalPrice)}</span>
                    </S.AddToCartButton>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ProductModal;
